import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { useGetPendingInspectionsQuery } from '../../redux/rtkQuery/apiSlice';
import { RootState } from '../../redux/root';
import { setPageIndex, setRowsCount } from '../../redux/slices/paginationSlice';
import { InspectionItemType } from '../../modules/clientModule/types';
import { ALL_PAGE_SIZE } from '../../constants';

// Columns whose value the backend `search` param (InspectionFilterSet.search_filter)
// matches on the same underlying fields, so we can narrow server-side before the
// exact per-column match below. Other columns (names, phones, jurisdiction) are
// composed/formatted differently on the client, so they fall back to fetching
// every pending row and filtering locally.
const SERVER_SEARCHABLE_COLUMNS = ['projectNamePermitName', 'permitNumber', 'projectAddress', 'id', 'inspectionType'];

const getServerSearch = (filters: { filterName: string; filterValue: string }[]): string | undefined => {
  const filter = filters.find(f => SERVER_SEARCHABLE_COLUMNS.includes(f.filterName) && f.filterValue?.trim());
  if (!filter) return undefined;
  // "Project | Permit" is joined client-side; search on the longest side.
  return filter.filterValue
    .split('|')
    .map(part => part.trim())
    .sort((a, b) => b.length - a.length)[0];
};

const useInspectionFilters = (dates: { fromDate: Date; toDate: Date }, toggleControl: boolean) => {
  const [params, setParams] = useState({});
  const currentUser = useSelector((s: RootState) => s.userAuth.info.id);
  const filters = useSelector((s: RootState) => s.filters.filters);
  const { pageIndex, pageSize } = useSelector((s: RootState) => s.pagination);
  const [rows, setRows] = useState<InspectionItemType[]>([]);
  const dispatch = useDispatch();
  const { data: pendingInspections, isFetching, isLoading } = useGetPendingInspectionsQuery(params, {
    skip: Object.keys(params).length === 0,
  });
  useEffect(() => {
    if (!pendingInspections) {
      setParamsValue(currentUser);
      return;
    }
    if (toggleControl) {
      setParamsValue(currentUser);
    } else {
      setParamsValue();
    }
  }, [toggleControl, dates, pageIndex, pageSize, filters]);

  // Column filters search across all pages, so start back at the first page.
  useEffect(() => {
    if (filters.length > 0 && pageIndex !== 0) {
      dispatch(setPageIndex(0));
    }
  }, [filters]);

  useEffect(() => {
    if (!pendingInspections) {
      return;
    }

    if (filters.length > 0) {
      const filteredRows = [];
      pendingInspections.records.forEach(insp => {
        let foundMatch = 0;
        filters.forEach(filter => {
         for (let i = 0; i < Object.keys(insp).length; i++) {
             if (`${insp[filter.filterName]}`.toLowerCase().indexOf(filter.filterValue) > -1) {
              foundMatch++;
              break;
             }
          }
        });
          if (foundMatch === filters.length) {
            filteredRows.push(insp);
          }
      });
      dispatch(setRowsCount(filteredRows.length));
      setRows(filteredRows);
    } else {
      // No filters
      dispatch(setRowsCount(pendingInspections.count));
      setRows(pendingInspections.records);
    }
  }, [pendingInspections, filters]);

  const setParamsValue = (inspectorId?: number) => {
    // The column filters below run on the client, so with a filter active we must
    // fetch every matching row — filtering only the current page missed anything
    // on later pages (e.g. an inspector with 111 pending rows searching for a
    // project that sorted onto page 2).
    const isFiltering = filters.length > 0;
    const _params = {
      limit: isFiltering ? ALL_PAGE_SIZE : pageSize,
      offset: isFiltering ? 0 : pageIndex * pageSize,
      search: isFiltering ? getServerSearch(filters) : undefined,
      is_deleted: false,
      ordering: '-is_emergency,follow_up',
      inspector: inspectorId,
      is_complete: false,
      date_from: undefined,
      date_to: undefined,
    };

    if (Object.keys(dates).length > 0) {
      _params.date_from = format(dates.fromDate, 'yyyy-MM-dd');
      _params.date_to = format(dates.toDate, 'yyyy-MM-dd');
    }
    setParams(_params);
  };
  return { setParamsValue, rows, isFetching };
};

export default useInspectionFilters;
